import { getMetaMaskConnectionMode, type MetaMaskConnectionMode } from './metamask-connection-mode';
import { WalletConnectionActionRequiredError } from './wallet-connection-error';

export type MetaMaskProvider = {
  isMetaMask?: boolean;
  providers?: MetaMaskProvider[];
  _metamask?: { isUnlocked?: () => Promise<boolean> };
  request: <T = unknown>(args: { method: string; params?: unknown }) => Promise<T>;
};

type Eip6963ProviderDetail = {
  info?: {
    name?: string;
    rdns?: string;
  };
  provider?: MetaMaskProvider;
};

function isMetaMaskProvider(provider?: MetaMaskProvider): provider is MetaMaskProvider {
  return provider?.isMetaMask === true && typeof provider.request === 'function';
}

function isMetaMaskProviderDetail(detail?: Eip6963ProviderDetail): detail is Eip6963ProviderDetail & { provider: MetaMaskProvider } {
  const rdns = typeof detail?.info?.rdns === 'string' ? detail.info.rdns.toLowerCase() : undefined;
  // Other wallets also set isMetaMask for compatibility. An announced name or
  // that flag alone must not override a different wallet's explicit identity.
  return isMetaMaskProvider(detail?.provider) && (rdns === 'io.metamask' || rdns === 'io.metamask.flask');
}

export function getInjectedMetaMaskCandidates(): MetaMaskProvider[] {
  if (typeof window === 'undefined') {
    return [];
  }

  const ethereum = (window as unknown as { ethereum?: MetaMaskProvider }).ethereum;

  if (!ethereum) {
    return [];
  }

  return [...new Set([...(Array.isArray(ethereum.providers) ? ethereum.providers : []), ethereum].filter(isMetaMaskProvider))];
}

/** A synchronous installation hint only; connection always performs discovery. */
export function hasMetaMaskProvider(): boolean {
  return getInjectedMetaMaskCandidates().length > 0;
}

/** Discover announced MetaMask identities before consulting the legacy injection. */
export async function findMetaMaskProviders(timeoutMs = 250, mode = getMetaMaskConnectionMode()): Promise<MetaMaskProvider[]> {
  if (typeof window === 'undefined') {
    return [];
  }

  const announced = new Set<MetaMaskProvider>();
  const announcedFlask = new Set<MetaMaskProvider>();
  const otherWallets = new Set<MetaMaskProvider>();
  await new Promise<void>((resolve) => {
    const finish = () => {
      window.removeEventListener('eip6963:announceProvider', onAnnounce as EventListener);
      resolve();
    };
    const onAnnounce = (event: Event) => {
      const detail = (event as CustomEvent<Eip6963ProviderDetail>).detail;

      if (isMetaMaskProviderDetail(detail)) {
        if (detail.info?.rdns?.toLowerCase() === 'io.metamask.flask') announcedFlask.add(detail.provider);
        else announced.add(detail.provider);
      } else if (detail?.provider && detail.info?.rdns) {
        otherWallets.add(detail.provider);
      }
    };

    window.addEventListener('eip6963:announceProvider', onAnnounce as EventListener);
    window.dispatchEvent(new Event('eip6963:requestProvider'));
    // Installed extensions normally answer synchronously. Keep the whole
    // response batch, but only wait when MetaMask has not announced yet.
    if (mode === 'flask' ? announcedFlask.size > 0 : announced.size > 0) finish();
    else window.setTimeout(finish, timeoutMs);
  });

  if (mode === 'default' && announced.size > 0) return [...announced];
  if (mode === 'flask' && announcedFlask.size > 0) return [...announcedFlask];

  // Older extensions may not announce. Read the client identity without
  // requesting accounts, installing a Snap, signing, or opening any wallet UI.
  const candidates = [...new Set([...announced, ...announcedFlask, ...getInjectedMetaMaskCandidates()])].filter(
    (provider) => !otherWallets.has(provider) && (mode !== 'default' || !announcedFlask.has(provider)),
  );
  const verified = await Promise.all(candidates.map((provider) => isLegacyMetaMaskProvider(provider, mode)));
  return candidates.filter((_, index) => verified[index]);
}

async function isLegacyMetaMaskProvider(provider: MetaMaskProvider, mode: MetaMaskConnectionMode): Promise<boolean> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const version = await Promise.race([
      Promise.resolve().then(() => provider.request({ method: 'web3_clientVersion' })),
      new Promise<undefined>((resolve) => {
        timeout = setTimeout(() => resolve(undefined), 1_000);
      }),
    ]);
    return typeof version === 'string' && /^MetaMask\//i.test(version) && /flask/i.test(version) === (mode === 'flask');
  } catch {
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

export async function detectMetaMaskProvider(timeoutMs = 250): Promise<boolean> {
  return (await findMetaMaskProviders(timeoutMs)).length > 0;
}

export async function getMetaMaskProvider(): Promise<MetaMaskProvider> {
  const [provider] = await findMetaMaskProviders();

  if (!provider) {
    throw new WalletConnectionActionRequiredError(
      'install-metamask',
      'wallet-not-installed',
      getMetaMaskConnectionMode() === 'flask'
        ? 'Please install MetaMask Flask to use the local Ducat Snap.'
        : 'Please install MetaMask to use the Ducat Snap.',
    );
  }

  return provider;
}
